import { memo } from 'react';
import Modal from 'react-modal';
import { useTranslator } from '@picsart/rc/services/localization';
import noop from '@picsart/rc/noop';
import IconCrossSmall from '@picsart/icons/IconCrossSmall';
import { Button, ButtonSizeMD, ButtonVariants } from '@picsart/button';

import { MODAL_MESSAGES, SURVEY_MONKEY_URLS } from 'configs/constants';
import Text from 'components/common/Text';

import useStyles from './styles';
import { ISuccessModalProps } from './types';
import testIds from './testIds';

const SuccessModal = ({
  title = 'Great edits! 🎉',
  isOpen = false,
  description = MODAL_MESSAGES.imageWasDownloaded,
  onContinue = noop,
  onCreateNew = noop,
  showSurvey = false,
  surveyTitle = 'Got a minute?',
  onSurveyClick = noop,
  onDescriptionClick = noop,
  surveyLink = SURVEY_MONKEY_URLS.batchEditorSecondSurvey,
  showClose = false,
}: ISuccessModalProps) => {
  const translate = useTranslator();
  const classes = useStyles();

  Modal.setAppElement('#modal-root');

  return (
    <Modal
      isOpen={isOpen}
      className={classes.downloadModal}
      overlayClassName={classes.downloadModalOverlay}
      onRequestClose={onContinue}
      testId={testIds.modal}
    >
      {showClose && (
        <button onClick={onContinue}>
          <IconCrossSmall className={classes.downloadCloseButton} />
        </button>
      )}
      <Text size="large" weight="bold" data-testid={testIds.modalTitle} data-test="modal root">{translate(title)}</Text>
      <Text size="base16" weight="medium" data-testid={testIds.modalDescription} color="secondary" className={classes.driveLink} onClick={onDescriptionClick}>
        {description}
      </Text>

      {showSurvey && (
        <div data-testid={testIds.survey}>
          <Text size="base16" weight="medium" color="secondary" data-testid={testIds.surveyTitle}>
            {translate(surveyTitle)}
          </Text>
          {' '}
          <Text
            data-testid={testIds.surveyLink}
            size="base16"
            weight="medium"
            color="brandSecondary"
            className={classes.surveyLink}
            tagType="a"
            href={surveyLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onSurveyClick}
          >
            {translate('Tell us about your experience.')}
          </Text>
        </div>
      )}
      <div className={classes.downloadFooter}>
        <Button
          size={ButtonSizeMD}
          variant={ButtonVariants.Outline}
          onClick={onContinue}
          dataTestId={testIds.continueButton}
        >
          {translate('Continue Editing')}
        </Button>
        <Button
          size={ButtonSizeMD}
          onClick={onCreateNew}
          dataTestId={testIds.createNewButton}
        >
          {translate('Start over')}
        </Button>
      </div>
    </Modal>
  );
};

export default memo(SuccessModal);
